/**
 * Backlog E Phase E.3 — Admin Dashboard.
 *
 * Replaces the AdminComingSoon stub at /admin/dashboard. Composed of:
 *   - KPITileGrid       : 6 tiles fed by GET /api/admin/metrics
 *   - ActivityFeed      : paginated GET /api/admin/activity
 *
 * Strict invariants (testable):
 *   - display_state="stripe_gated" tiles ALWAYS render "—". The component
 *     literally never reads tile.value for gated tiles. Even if backend
 *     somehow leaks a number on a gated tile, FE renders "—".
 *   - display_state="live" with value=null (div/0) renders "—".
 *   - GMV tile carries a tooltip via an ⓘ affordance.
 *   - Impersonation toggle mutes all 3 impersonation event types as one
 *     logical group via sources= filter (per E.2 Q4 ack).
 *
 * Brand tokens: bg-pp-v3-bg, border-pp-border, text-pp-text, var(--pp-orange).
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { Info, RefreshCw, Loader2 } from "lucide-react";


function _formatCents(cents) {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
  }).format(cents / 100);
}

function _formatPercent(value) {
  if (value === null || value === undefined) return "—";
  return `${Math.round(value * 100)}%`;
}

function _formatInt(value) {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("en-US");
}

/**
 * `_formatRelativeTime` — relative timestamp ("2h ago" / "3d ago").
 *
 * NEGATIVE-TIMESTAMP GUARD: if the input timestamp is in the FUTURE
 * (e.g. demo_analytics seed records sprinkled forward in time, clock
 * skew between client + server, or any other upstream weirdness) the
 * raw elapsed-seconds value would otherwise be negative and surface
 * as `-1725348s ago` — visibly broken. We clamp `seconds < 5` (which
 * covers any negative value AND the "just-now" edge) to the friendly
 * literal `"just now"` so the display layer NEVER renders a negative
 * count regardless of what upstream data sends.
 *
 * Exported so the negative-timestamp Jest case can pin the invariant
 * (`AdminDashboardPage.test.jsx::formatRelativeTime: future-dated
 * input clamps to "just now"`).
 */
export function _formatRelativeTime(isoString) {
  if (!isoString) return "";
  const then = new Date(isoString).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);
  // Future-dated input → clamp. Covers seconds < 0 AND the "moments
  // ago" edge (0..4s) which would otherwise read as "0s ago".
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}


// ---------------------------------------------------------------------------
// KPITile — strict-contract component
// ---------------------------------------------------------------------------
function KPITile({ label, tile, formatter, testid }) {
  const isGated = tile?.display_state === "stripe_gated";
  // CRITICAL: never read tile.value when gated. Even if backend leaks
  // a value field (which Pydantic should prevent server-side), FE
  // refuses to render it.
  let displayValue;
  if (isGated) {
    displayValue = "—";
  } else if (tile?.value === null || tile?.value === undefined) {
    displayValue = "—";
  } else {
    displayValue = formatter ? formatter(tile.value) : String(tile.value);
  }

  return (
    <Card data-testid={testid} className="bg-pp-v3-bg border-pp-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-pp-text text-sm font-medium flex items-center gap-2">
          <span>{label}</span>
          {tile?.tooltip ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    data-testid={`${testid}-info`}
                    className="cursor-help inline-flex"
                    aria-label={`${label} info`}
                  >
                    <Info className="w-3.5 h-3.5 text-pp-text/50" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {tile.tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          data-testid={`${testid}-value`}
          className="text-3xl font-semibold text-pp-text tabular-nums"
        >
          {displayValue}
        </div>
        {isGated ? (
          <p
            data-testid={`${testid}-note`}
            className="text-xs text-pp-text/50 mt-1"
          >
            {tile.note}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// KPITileGrid
// ---------------------------------------------------------------------------
function KPITileGrid({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get("/admin/metrics")
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "Failed to load metrics");
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) {
    return (
      <div data-testid="kpi-grid-loading" className="flex items-center gap-2 text-pp-text/60">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading metrics…
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="kpi-grid-error" className="text-sm text-red-500">
        {error}
      </div>
    );
  }
  const tiles = data?.tiles || {};
  return (
    <div
      data-testid="kpi-grid"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      <KPITile
        testid="kpi-tile-active-bookings"
        label="Active bookings"
        tile={tiles.active_bookings}
        formatter={_formatInt}
      />
      <KPITile
        testid="kpi-tile-completion-rate"
        label="Completion rate · 30d"
        tile={tiles.completion_rate_30d}
        formatter={_formatPercent}
      />
      <KPITile
        testid="kpi-tile-gmv-mtd"
        label="GMV · MTD"
        tile={tiles.gmv_mtd_cents}
        formatter={_formatCents}
      />
      <KPITile
        testid="kpi-tile-revenue-mtd"
        label="Revenue · MTD"
        tile={tiles.revenue_mtd_cents}
        formatter={_formatCents}
      />
      <KPITile
        testid="kpi-tile-revenue-7d"
        label="Revenue · 7d"
        tile={tiles.revenue_7d_cents}
        formatter={_formatCents}
      />
      <KPITile
        testid="kpi-tile-avg-booking-value"
        label="Avg booking value · 30d"
        tile={tiles.avg_booking_value_30d_cents}
        formatter={_formatCents}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// ActivityRow
// ---------------------------------------------------------------------------
function ActivityRow({ item }) {
  const sourceColor = {
    bookings:      "border-l-orange-500",
    reviews:       "border-l-yellow-500",
    users:         "border-l-blue-500",
    impersonation: "border-l-purple-500",
  }[item.source] || "border-l-pp-border";

  return (
    <li
      data-testid={`activity-item-${item.id}`}
      className={`pl-3 py-2 border-l-2 ${sourceColor} bg-pp-v3-bg/40`}
    >
      <div className="text-sm text-pp-text">{item.summary}</div>
      <div className="flex items-center gap-2 mt-0.5">
        <span
          className="text-xs text-pp-text/50"
          title={item.occurred_at}
        >
          {_formatRelativeTime(item.occurred_at)}
        </span>
        <span className="text-xs text-pp-text/40">·</span>
        <span className="text-xs text-pp-text/40 uppercase tracking-wider">
          {item.source}
        </span>
        {item.semantic_label ? (
          <span className="text-xs text-pp-text/50">
            · {item.semantic_label}
          </span>
        ) : null}
      </div>
    </li>
  );
}


// ---------------------------------------------------------------------------
// ActivityFeed
// ---------------------------------------------------------------------------
function ActivityFeed({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [impersonationOn, setImpersonationOn] = useState(true);

  const buildSourcesParam = (impOn) => (
    impOn ? undefined : "bookings,reviews,users"
  );

  const fetchPage = async ({ reset = false, impOn = impersonationOn } = {}) => {
    setLoadingMore(true);
    try {
      const params = { limit: 20 };
      const sources = buildSourcesParam(impOn);
      if (sources) params.sources = sources;
      if (!reset && cursor) params.cursor = cursor;
      const res = await api.get("/admin/activity", { params });
      setItems(reset ? res.data.items : [...items, ...res.data.items]);
      setCursor(res.data.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  };

  // Reset on refreshKey OR impersonation toggle
  useEffect(() => {
    setItems([]);
    setCursor(null);
    fetchPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const onToggleImpersonation = async (checked) => {
    setImpersonationOn(checked);
    setItems([]);
    setCursor(null);
    await fetchPage({ reset: true, impOn: checked });
  };

  const hasMore = !!cursor;

  return (
    <Card data-testid="activity-feed" className="bg-pp-v3-bg border-pp-border">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle className="text-pp-text">Recent activity</CardTitle>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-pp-text">
            <Switch
              data-testid="activity-impersonation-toggle"
              checked={impersonationOn}
              onCheckedChange={onToggleImpersonation}
            />
            Impersonation
          </label>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div data-testid="activity-empty" className="text-sm text-pp-text/50">
            No activity yet.
          </div>
        ) : (
          <ul data-testid="activity-list" className="space-y-1.5">
            {items.map((it) => <ActivityRow key={it.id} item={it} />)}
          </ul>
        )}
        {hasMore ? (
          <div className="mt-4">
            <Button
              data-testid="activity-load-more"
              variant="secondary"
              size="sm"
              disabled={loadingMore}
              onClick={() => fetchPage({ reset: false })}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// AdminDashboardPage
// ---------------------------------------------------------------------------
export default function AdminDashboardPage() {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-pp-text">Dashboard</h2>
          <p className="text-sm text-pp-text/60 mt-1">
            Platform-wide metrics &amp; recent activity.
          </p>
        </div>
        <Button
          data-testid="dashboard-refresh"
          variant="ghost"
          size="sm"
          onClick={() => setRefreshKey((k) => k + 1)}
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </header>
      <KPITileGrid refreshKey={refreshKey} />
      <ActivityFeed refreshKey={refreshKey} />
    </div>
  );
}
